import { useApi } from './useApi.js'
import { isOpenToVisitors, isPortalPath } from '../router/public-routes.js'

/**
 * Composable pour charger le script Google Maps (état partagé par module : le
 * script n'est injecté qu'une fois, quel que soit le nombre de cartes).
 *
 * `source` dit d'où vient la clé :
 * - `'internal'` (écrans du back-office) : route authentifiée, repli sur la
 *   route publique si elle répond en erreur ;
 * - `'public'` (écrans ouverts aux visiteurs ou aux portails : `/embed/map`,
 *   inscription formateur, espace formateur) : route publique SEULE. Demander
 *   d'abord la route authentifiée y coûtait un 401 à chaque affichage, faute
 *   de session de back-office (règle P14 de docs/PUBLIQUES-V2.md).
 *
 * Sans `source`, la page courante tranche : même règle que le reste de
 * l'application (`router/public-routes.js`).
 */
export const INTERNAL_KEY_ENDPOINT = '/api/config/google-maps-key'
export const PUBLIC_KEY_ENDPOINT = '/api/public/config/google-maps-key'

export function sourceFromPath(path) {
  const pathname = String(path || '').split('?')[0]
  // `isPortalPath`, pas un `startsWith` nu : le préfixe compte seul ou suivi
  // d'une barre, `/espace-formateurXYZ` n'est pas une page de portail.
  const horsBackOffice = isOpenToVisitors(pathname) || isPortalPath(pathname)
  return horsBackOffice ? 'public' : 'internal'
}

let googleMapsLoaded = false
let googleMapsLoading = false
let loadCallbacks = []

const currentPath = () => (typeof window === 'undefined' ? '' : window.location?.pathname)

export function useGoogleMapsLoader({ source = sourceFromPath(currentPath()) } = {}) {
  const { get: apiGet } = useApi()

  const fetchKey = () => {
    const publicKey = () => apiGet(PUBLIC_KEY_ENDPOINT, { showErrorToast: false })
    if (source === 'public') return publicKey()
    return apiGet(INTERNAL_KEY_ENDPOINT, {
      showErrorToast: false,
      errorMessage: 'Clé API Google Maps non configurée'
    }).catch(publicKey)
  }

  const loadGoogleMapsScript = () => {
    return new Promise((resolve, reject) => {
      if (googleMapsLoaded && window.google?.maps?.places) {
        resolve()
        return
      }

      if (googleMapsLoading) {
        loadCallbacks.push({ resolve, reject })
        return
      }

      googleMapsLoading = true

      fetchKey()
        .then(data => {
          if (!data?.apiKey) {
            throw new Error('Clé API Google Maps non disponible')
          }

          const script = document.createElement('script')
          script.src = `https://maps.googleapis.com/maps/api/js?key=${data.apiKey}&libraries=places&language=fr`
          script.async = true
          script.defer = true

          script.onload = () => {
            googleMapsLoaded = true
            googleMapsLoading = false
            resolve()
            loadCallbacks.forEach(cb => cb.resolve())
            loadCallbacks = []
          }

          script.onerror = () => {
            googleMapsLoading = false
            const err = new Error('Erreur lors du chargement de Google Maps')
            reject(err)
            loadCallbacks.forEach(cb => cb.reject(err))
            loadCallbacks = []
          }

          document.head.appendChild(script)
        })
        .catch(err => {
          googleMapsLoading = false
          reject(err)
          loadCallbacks.forEach(cb => cb.reject(err))
          loadCallbacks = []
        })
    })
  }

  return { loadGoogleMapsScript }
}
